import { useMemo } from 'react'

import _ from 'lodash'

import { useDigitalProductContext } from '@/shared/contexts/DigitalProductContext'
import { useRecord } from '@/shared/hooks'

export type RankingEntry = {
  id: number
  rank: number
  points: number
  me: boolean
  user_progress:
    | {
        avatar?: Record<string, unknown>
        user?: {
          id: number
          name: string
          avatar?: { file?: { url?: string } }
        }
      }
    | undefined
}

const TOP_TIER_SIZE = 10

export const useRankingData = () => {
  const {
    config: { apiBasePathWithId }
  } = useDigitalProductContext()

  const { record, isLoading } = useRecord({
    r: `${apiBasePathWithId}/insights/dashboard`,
    forceIdPresence: false
  }) as { record: any; isLoading: boolean }

  return useMemo(() => {
    const ranking = _.get(record, 'points_ranking') ?? []
    const myRanking = _.get(record, 'my_points_ranking') ?? null

    const entries: RankingEntry[] = _.sortBy(ranking, 'points')
      .reverse()
      .map((item: any) => ({
        ...item,
        id: item?.user_progress?.user?.id ?? item.rank,
        me:
          item?.user_progress?.user?.id ===
          myRanking?.user_progress?.user?.id
      }))

    const top3 = entries.slice(0, 3)
    const rest = entries.slice(3)
    const meIsListed = entries.some(entry => entry.me)

    // Meta de avanço: quanta experiência falta para entrar no Top 10.
    const topTierThreshold =
      entries.length >= TOP_TIER_SIZE
        ? entries[TOP_TIER_SIZE - 1].points
        : null

    const myPoints = myRanking?.points ?? 0
    const isOnTopTier =
      myRanking != null &&
      myRanking.rank != null &&
      myRanking.rank <= TOP_TIER_SIZE

    const topTierGap =
      myRanking && !isOnTopTier && topTierThreshold != null
        ? Math.max(topTierThreshold - myPoints + 1, 0)
        : null

    const topTierPercent =
      topTierGap != null && topTierThreshold && topTierThreshold > 0
        ? Math.min(Math.round((myPoints / topTierThreshold) * 100), 99)
        : null

    return {
      entries,
      top3,
      rest,
      myRanking,
      meIsListed,
      isOnTopTier,
      topTierGap,
      topTierPercent,
      isEmpty: entries.length === 0 && !myRanking,
      isLoading
    }
  }, [record, isLoading])
}
